import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { SelectInput, TextInput } from "@/components/ui/Field";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError } from "@/lib/apiClient";
import {
  EMPTY_ZAHLUNGSBEDINGUNGEN_OVERRIDE,
  listKundeProjektZahlungsbedingungen,
  updateKundeZahlungsbedingungen,
  updateProjektZahlungsbedingungen,
  ZAHLUNGSART_OPTIONS,
  type Zahlungsart,
  type ZahlungsbedingungenOverride,
  type ZahlungsbedingungenStandard,
  type ProjektZahlungsbedingungen,
} from "@/lib/kontakte";

interface KundeZahlungsbedingungenSectionProps {
  geschaeftspartnerId: number;
  override: ZahlungsbedingungenOverride | null;
  effektiv: ZahlungsbedingungenStandard | null;
  onUpdated: (next: {
    override: ZahlungsbedingungenOverride | null;
    effektiv: ZahlungsbedingungenStandard | null;
  }) => void;
}

type OverrideForm = {
  useCustom: boolean;
  zahlungsart: Zahlungsart;
  zahlungsziel_tage: string;
  skonto_prozent: string;
  skontoziel_tage: string;
};

function overrideToForm(
  override: ZahlungsbedingungenOverride | null,
  effektiv: ZahlungsbedingungenStandard | null,
): OverrideForm {
  const hasCustom = Boolean(
    override &&
      (override.zahlungsart != null ||
        override.zahlungsziel_tage != null ||
        override.skonto_prozent != null ||
        override.skontoziel_tage != null),
  );
  return {
    useCustom: hasCustom,
    zahlungsart: (override?.zahlungsart || effektiv?.zahlungsart || "rechnung") as Zahlungsart,
    zahlungsziel_tage: String(
      override?.zahlungsziel_tage ?? effektiv?.zahlungsziel_tage ?? 14,
    ),
    skonto_prozent: String(override?.skonto_prozent ?? effektiv?.skonto_prozent ?? 0),
    skontoziel_tage: String(override?.skontoziel_tage ?? effektiv?.skontoziel_tage ?? 0),
  };
}

function formToOverride(form: OverrideForm): ZahlungsbedingungenOverride {
  if (!form.useCustom) {
    return { ...EMPTY_ZAHLUNGSBEDINGUNGEN_OVERRIDE };
  }
  const art = form.zahlungsart === "vorkasse" ? "vorkasse" : "rechnung";
  return {
    zahlungsart: art,
    zahlungsziel_tage: art === "vorkasse" ? 0 : Math.max(0, Number(form.zahlungsziel_tage) || 0),
    skonto_prozent: Math.max(0, Number(form.skonto_prozent) || 0),
    skontoziel_tage: Math.max(0, Number(form.skontoziel_tage) || 0),
  };
}

function formatEffektiv(eff: ZahlungsbedingungenStandard | null | undefined): string {
  if (!eff) return "—";
  const art = eff.zahlungsart === "vorkasse" ? "Vorkasse" : "Rechnung";
  const ziel =
    eff.zahlungsart === "vorkasse" ? "sofort" : `${eff.zahlungsziel_tage} Tage`;
  const skonto =
    eff.skonto_prozent > 0
      ? ` · ${String(eff.skonto_prozent).replace(".", ",")}% Skonto / ${eff.skontoziel_tage} Tage`
      : "";
  return `${art}, ${ziel}${skonto}`;
}

type ProjektForm = OverrideForm & { auftrag_id: number };

export function KundeZahlungsbedingungenSection({
  geschaeftspartnerId,
  override,
  effektiv,
  onUpdated,
}: KundeZahlungsbedingungenSectionProps) {
  const baseline = useMemo(
    () => overrideToForm(override, effektiv),
    [override, effektiv],
  );
  const [form, setForm] = useState<OverrideForm>(baseline);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [projects, setProjects] = useState<ProjektZahlungsbedingungen[]>([]);
  const [projectForms, setProjectForms] = useState<Record<number, ProjektForm>>({});
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [savingProjectId, setSavingProjectId] = useState<number | null>(null);
  const [projectError, setProjectError] = useState<string | null>(null);

  const dirty = useMemo(
    () => JSON.stringify(form) !== JSON.stringify(baseline),
    [form, baseline],
  );

  useEffect(() => {
    if (!dirty) setForm(baseline);
  }, [baseline, dirty]);

  const loadProjects = useCallback(async () => {
    setProjectsLoading(true);
    setProjectError(null);
    try {
      const rows = await listKundeProjektZahlungsbedingungen(geschaeftspartnerId);
      setProjects(rows);
      const forms: Record<number, ProjektForm> = {};
      for (const row of rows) {
        forms[row.auftrag_id] = {
          auftrag_id: row.auftrag_id,
          ...overrideToForm(row.override, row.effektiv),
        };
      }
      setProjectForms(forms);
    } catch (err) {
      setProjectError(
        err instanceof ApiError
          ? err.message
          : "Projekt-Zahlungsziele konnten nicht geladen werden.",
      );
      setProjects([]);
      setProjectForms({});
    } finally {
      setProjectsLoading(false);
    }
  }, [geschaeftspartnerId]);

  useEffect(() => {
    void loadProjects();
  }, [loadProjects]);

  async function handleSaveKunde() {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const payload = formToOverride(form);
      const updated = await updateKundeZahlungsbedingungen(geschaeftspartnerId, payload);
      onUpdated({
        override: updated.zahlungsbedingungen,
        effektiv: updated.zahlungsbedingungen_effektiv,
      });
      setSuccess("Kunden-Zahlungsziele gespeichert.");
      await loadProjects();
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : "Kunden-Zahlungsziele konnten nicht gespeichert werden.",
      );
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveProjekt(auftragId: number) {
    const current = projectForms[auftragId];
    if (!current) return;
    setSavingProjectId(auftragId);
    setProjectError(null);
    try {
      const saved = await updateProjektZahlungsbedingungen(
        auftragId,
        formToOverride(current),
      );
      setProjects((rows) =>
        rows.map((row) => (row.auftrag_id === auftragId ? saved : row)),
      );
      setProjectForms((prev) => ({
        ...prev,
        [auftragId]: {
          auftrag_id: auftragId,
          ...overrideToForm(saved.override, saved.effektiv),
        },
      }));
    } catch (err) {
      setProjectError(
        err instanceof ApiError
          ? err.message
          : "Projekt-Zahlungsziele konnten nicht gespeichert werden.",
      );
    } finally {
      setSavingProjectId(null);
    }
  }

  function patchProjekt(auftragId: number, patch: Partial<OverrideForm>) {
    setProjectForms((prev) => {
      const current = prev[auftragId];
      if (!current) return prev;
      const next = { ...current, ...patch };
      if (patch.zahlungsart === "vorkasse") {
        next.zahlungsziel_tage = "0";
      }
      return { ...prev, [auftragId]: next };
    });
  }

  return (
    <>
      <Card
        title="Zahlungsziele"
        description="Ohne eigene Werte gilt der Firmen-Standard. Projekte können zusätzlich eigene Zahlungsziele haben."
        actions={
          <Button onClick={() => void handleSaveKunde()} disabled={saving || !dirty}>
            {saving ? "Speichern…" : "Speichern"}
          </Button>
        }
      >
        {error ? (
          <Alert variant="error" title="Fehler" className="mb-4">
            {error}
          </Alert>
        ) : null}
        {success ? (
          <Alert variant="success" title="Gespeichert" className="mb-4">
            {success}
          </Alert>
        ) : null}

        <label className="mb-4 flex cursor-pointer items-center gap-3">
          <input
            type="checkbox"
            checked={form.useCustom}
            onChange={(e) => setForm((f) => ({ ...f, useCustom: e.target.checked }))}
            className="h-4 w-4 rounded border-separator"
          />
          <span className="text-sm text-label">Eigene Zahlungsziele für diesen Kunden</span>
        </label>

        {!form.useCustom ? (
          <div className="rounded-ios border border-separator/70 bg-fill-quaternary/40 px-4 py-3">
            <div className="text-xs font-medium uppercase tracking-wide text-label-tertiary">
              Aktuell wirksam (Firmen-Standard)
            </div>
            <div className="mt-1 text-sm font-medium text-label">{formatEffektiv(effektiv)}</div>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <SelectInput
              label="Zahlungsart"
              value={form.zahlungsart}
              onChange={(e) => {
                const zahlungsart = e.target.value as Zahlungsart;
                setForm((f) => ({
                  ...f,
                  zahlungsart,
                  zahlungsziel_tage: zahlungsart === "vorkasse" ? "0" : f.zahlungsziel_tage,
                }));
              }}
            >
              {ZAHLUNGSART_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </SelectInput>
            <TextInput
              label="Zahlungsziel (Tage)"
              type="number"
              min={0}
              value={form.zahlungsziel_tage}
              disabled={form.zahlungsart === "vorkasse"}
              onChange={(e) => setForm((f) => ({ ...f, zahlungsziel_tage: e.target.value }))}
            />
            <TextInput
              label="Skonto %"
              type="number"
              min={0}
              step="0.01"
              value={form.skonto_prozent}
              onChange={(e) => setForm((f) => ({ ...f, skonto_prozent: e.target.value }))}
            />
            <TextInput
              label="Skontoziel (Tage)"
              type="number"
              min={0}
              value={form.skontoziel_tage}
              onChange={(e) => setForm((f) => ({ ...f, skontoziel_tage: e.target.value }))}
            />
          </div>
        )}
      </Card>

      <Card
        title="Projekt-Zahlungsziele"
        description="Optionale Abweichungen je Projekt. Ohne Haken gilt der Kunden- bzw. Firmen-Standard."
      >
        {projectError ? (
          <Alert variant="error" title="Fehler" className="mb-4">
            {projectError}
          </Alert>
        ) : null}
        {projectsLoading ? (
          <div className="flex justify-center py-6">
            <Spinner />
          </div>
        ) : projects.length === 0 ? (
          <p className="text-sm text-label-secondary">Keine Projekte für diesen Kunden.</p>
        ) : (
          <div className="space-y-4">
            {projects.map((row) => {
              const pf = projectForms[row.auftrag_id];
              if (!pf) return null;
              const baselineForm = overrideToForm(row.override, row.effektiv);
              const projectDirty =
                JSON.stringify({ ...pf, auftrag_id: undefined }) !==
                JSON.stringify({ ...baselineForm, auftrag_id: undefined });
              return (
                <div
                  key={row.auftrag_id}
                  className="rounded-ios border border-separator/70 p-4"
                >
                  <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <Link
                        to={`/projekte/${row.auftrag_id}`}
                        className="font-medium text-accent hover:underline"
                      >
                        {row.auftragsnummer || `Projekt #${row.auftrag_id}`}
                      </Link>
                      {row.beschreibung ? (
                        <p className="mt-0.5 text-sm text-label-secondary">{row.beschreibung}</p>
                      ) : null}
                      <p className="mt-1 text-xs text-label-tertiary">
                        Wirksam: {formatEffektiv(row.effektiv)}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => void handleSaveProjekt(row.auftrag_id)}
                      disabled={savingProjectId === row.auftrag_id || !projectDirty}
                    >
                      {savingProjectId === row.auftrag_id ? "Speichern…" : "Speichern"}
                    </Button>
                  </div>
                  <label className="mb-3 flex cursor-pointer items-center gap-3">
                    <input
                      type="checkbox"
                      checked={pf.useCustom}
                      onChange={(e) =>
                        patchProjekt(row.auftrag_id, { useCustom: e.target.checked })
                      }
                      className="h-4 w-4 rounded border-separator"
                    />
                    <span className="text-sm text-label">Eigene Zahlungsziele für dieses Projekt</span>
                  </label>
                  {pf.useCustom ? (
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                      <SelectInput
                        label="Zahlungsart"
                        value={pf.zahlungsart}
                        onChange={(e) =>
                          patchProjekt(row.auftrag_id, {
                            zahlungsart: e.target.value as Zahlungsart,
                          })
                        }
                      >
                        {ZAHLUNGSART_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </SelectInput>
                      <TextInput
                        label="Zahlungsziel (Tage)"
                        type="number"
                        min={0}
                        value={pf.zahlungsziel_tage}
                        disabled={pf.zahlungsart === "vorkasse"}
                        onChange={(e) =>
                          patchProjekt(row.auftrag_id, {
                            zahlungsziel_tage: e.target.value,
                          })
                        }
                      />
                      <TextInput
                        label="Skonto %"
                        type="number"
                        min={0}
                        step="0.01"
                        value={pf.skonto_prozent}
                        onChange={(e) =>
                          patchProjekt(row.auftrag_id, { skonto_prozent: e.target.value })
                        }
                      />
                      <TextInput
                        label="Skontoziel (Tage)"
                        type="number"
                        min={0}
                        value={pf.skontoziel_tage}
                        onChange={(e) =>
                          patchProjekt(row.auftrag_id, {
                            skontoziel_tage: e.target.value,
                          })
                        }
                      />
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </>
  );
}
